
import React from 'react';
import { Calendar, MapPin, BadgeCheck } from 'lucide-react';
import { CommunityEvent } from '../../../core/types/events';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Props {
  event: CommunityEvent;
  variant?: 'default' | 'compact' | 'featured';
}

export const EventCard: React.FC<Props> = ({ event, variant = 'default' }) => {
  const isFeatured = variant === 'featured';
  
  const formatDateRange = (start: Date, end: Date) => {
    const startDate = format(start, "dd 'de' MMM", { locale: ptBR });
    const endDate = format(end, "dd 'de' MMM", { locale: ptBR });
    
    if (startDate === endDate) return startDate;
    return `${startDate} a ${endDate}`;
  };

  return (
    <div className={`bg-brand-charcoal/30 rounded-3xl overflow-hidden border border-white/10 hover:border-brand-gold/50 transition-all group h-full flex flex-col backdrop-blur-3xl ${
      isFeatured ? 'border-brand-gold/30 ring-4 ring-brand-gold/5' : ''
    }`}>
      {/* Imagem de Capa */}
      <div className={`relative bg-white/5 ${isFeatured ? 'h-64' : 'h-48'} overflow-hidden`}>
        {event.coverImage ? (
          <img 
            src={event.coverImage} 
            alt={event.title} 
            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/10 text-4xl">
            📅
          </div>
        )}
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          <span className="bg-brand-midnight/80 backdrop-blur text-white px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border border-white/10 shadow-xl">
            {event.type === 'tournament' ? '🏆 Torneio' : 
             event.type === 'festival' ? '🎉 Festival' : 
             event.type === 'workshop' ? '👨‍🏫 Curso' : '📅 Evento'}
          </span>
        </div>
      </div>
      
      {/* Conteúdo */}
      <div className="p-5 flex flex-col flex-grow">
        <div className="flex items-start justify-between mb-2">
           <div className="text-[10px] text-brand-gold font-black uppercase tracking-[0.2em] flex items-center gap-2">
             <Calendar size={12} />
             {formatDateRange(event.startDate, event.endDate)}
           </div>
        </div>
        
        <h3 className={`font-black text-white mb-2 group-hover:text-brand-gold transition-colors font-display uppercase tracking-tight ${
          isFeatured ? 'text-3xl' : 'text-xl'
        }`}>
          {event.title}
        </h3>
        
        <p className="text-white/40 text-sm line-clamp-2 mb-6 flex-grow font-medium leading-relaxed italic">
          {event.description}
        </p>
        
        <div className="space-y-2 mt-auto">
          <div className="flex items-center text-[10px] font-black uppercase tracking-widest text-white/20">
            <MapPin size={12} className="mr-2 text-brand-orange" />
            <span className="truncate">{event.location.city} - {event.location.state}</span>
          </div>
          
          <div className="flex items-center justify-between pt-5 border-t border-white/5">
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-black uppercase tracking-widest text-white/20">Org:</span>
              <span className="text-[10px] font-black uppercase tracking-tight text-white/60 flex items-center gap-1">
                {event.organizerName}
                {event.isVerified && <BadgeCheck size={12} className="text-brand-gold" />}
              </span>
            </div>
            
            {event.price ? (
              <span className="font-black text-brand-gold text-lg">
                {event.price.amount === 0 ? 'Grátis' : 
                 new Intl.NumberFormat('pt-BR', { style: 'currency', currency: event.price.currency, maximumFractionDigits: 0 }).format(event.price.amount)}
              </span>
            ) : (
              <span className="text-[10px] font-black uppercase tracking-widest text-brand-gold/40">Consulte</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
